import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

// Cross-page supplier onboarding tour store + step definitions.
//
// The tour spans two routes – the portal home (/portal) and the compliance
// page (/portal/compliance) – so its open-state and current step must
// survive the route change that unmounts one page and mounts the next.
// React Router navigations are client-side (no full page reload), so this
// module-level store persists across them. Whichever portal page is mounted
// hosts the spotlight overlay (SupplierTour) and reads the same step here,
// so the walkthrough continues seamlessly as it hops between pages.

let state = { open: false, step: 0 };
const listeners = new Set();

function emit() {
  for (const listener of listeners) listener(state);
}

export function getTourState() {
  return state;
}

export function subscribeTour(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Open from step 0, advance/jump to a step, or close. closeTour resets to 0
// so the next open (auto-run or replay) starts clean.
export function openTour() { state = { open: true, step: 0 }; emit(); }
export function setTourStep(step) { state = { ...state, step }; emit(); }
export function closeTour() { state = { open: false, step: 0 }; emit(); }

// React hook – subscribe a component to the tour store.
export function useTour() {
  const [snapshot, setSnapshot] = useState(getTourState);
  useEffect(() => subscribeTour(setSnapshot), []);
  return snapshot;
}

// Persist "seen" on finish or skip. Idempotent and safe to call from
// whichever page the tour happens to end on (the final step returns to the
// portal, but a supplier can Skip while on the compliance leg). Callers that
// hold the supplier in local state can update it optimistically alongside
// this; the write itself just stamps the suppliers row.
export async function recordTourSeen(supplierId) {
  if (!supplierId) return;
  await supabase.from('suppliers')
    .update({ onboarding_tour_seen: true, onboarding_tour_completed_at: new Date().toISOString() })
    .eq('id', supplierId);
}

// Ordered step list. Each step names the route it lives on; the tour
// navigates between routes automatically and positions its card beside the
// target once that page has loaded. Steps without a target render centred.
//
// Flow: the portal home is covered first, then the tour dips into the real
// compliance page to show the supplier their compliance summary + documents,
// before returning to the portal to finish. A step whose target is absent on
// this render (e.g. a brand-new supplier with no brief card) is skipped
// gracefully by SupplierTour. Copy is plain-English, British, professional.
// v3 (cross-page) flow, 10 steps: the dashboard is covered first (welcome →
// briefs → respond → compliance badge → recognition → ⓘ), then the tour
// navigates to /portal/compliance for the documents area, delegation and the
// account menu, then returns to /portal for a centred wrap-up that carries the
// "show these tips" toggle. The account menu step anchors to the header chip,
// present on both pages.
//
// v3 (2026-08-14) brought the tour back in line with a portal that had moved
// on underneath it. The tour is CODE, not a recording — it can be revised any
// time, and it should be revised whenever the portal gains something a supplier
// has to discover for themselves. What changed:
//   - "Upload your documents" became "Your documents". There are four ways to
//     satisfy one now and the step taught only the first.
//   - Delegation got a step. It is a per-document control, so a supplier who
//     never opens the right row never learns it exists.
//   - Asking a question, and documents expiring, are both named.
// The documents and delegation steps deliberately share one target: the second
// is a capability within the area the first introduces, and the portal has no
// separate anchor for it.
export const SUPPLIER_TOUR_STEPS = [
  { key: 'welcome',     route: '/portal',                                   title: 'Welcome to Supply Pilot',       body: "Here's a quick tour of the partner portal. You can skip any time." },
  { key: 'briefs',      route: '/portal',            target: '#t-tabs',        title: 'Your briefs, tracked',          body: 'These are opportunities Fusion 2K has invited you to quote for. The tabs follow each brief from awaiting your response through to awarded.' },
  // Asking a question is named here rather than given its own step: the Q&A
  // lives on the brief detail page, which this tour never visits, and a step
  // pointing at a page we don't open would be worse than a sentence.
  { key: 'respond',     route: '/portal',            target: '#t-card',        title: 'Responding to a brief',         body: "Open any brief for the full detail, then accept to send a quote – or decline if it isn't right for you. If anything is unclear, ask a question on the brief rather than guessing." },
  // Expiry is named because it is the commonest reason a supplier who was
  // green goes red without doing anything: certificates lapse. Better learned
  // on day one than discovered when a brief can't be awarded.
  { key: 'compliance',  route: '/portal',            target: '#t-compliance',  title: 'Your compliance, at a glance',  body: "Green means you're ready to be awarded work. Amber or red means something needs your attention – usually a document that's missing, or one that's expired and needs supplying again." },
  // "shows to Fusion 2K" only, and "complete" not "approve": approving is
  // Fusion's act, and the badges are not shown to clients (Rob, 2026-08-14).
  // Same correction as the portal's own eco card – this file sits outside the
  // portal page list, which is why it kept the old claim and an em dash after
  // both had been fixed there.
  { key: 'recognition', route: '/portal',            target: '#t-recognition', title: 'Earn recognition',              body: 'Complete your eco documents to grow your Oak towards Oak Status – recognition shows to Fusion 2K on every brief.' },
  { key: 'info',        route: '/portal',            target: '#t-info',        title: 'Quick explanations, any time',  body: 'Spot a teal ⓘ anywhere in the portal? Hover or tap it for a plain-English explanation.' },
  // NOT "upload your documents" any more. There are four ways to satisfy a
  // document — upload a file, acknowledge it, complete a form, or sign a PDF —
  // and this step used to teach only the first. lib/complianceActions exists
  // precisely to stop this: "telling someone to sign an insurance certificate
  // is exactly the bug this module exists to prevent", and the tour was
  // committing the mirror image of it on every supplier's first visit.
  //
  // So it does what the chase emails do when a list is mixed: stays generic
  // and points at the row, because the row already says what it needs.
  { key: 'documents',   route: '/portal/compliance', target: '#t-compliance-docs', title: 'Your documents',            body: "This is your compliance area. Each document says what it needs – some you upload, some you read and sign, some are a short form to fill in. Fusion 2K needs them approved before they can award you work." },
  // Delegation shipped 2026-08-13 and is genuinely hard to discover: it's a
  // per-document control, so a supplier who never opens the right row never
  // learns it exists — and the person who holds the insurance certificate is
  // very often not the person who logs in.
  { key: 'delegate',    route: '/portal/compliance', target: '#t-compliance-docs', title: "Not the right person? Pass it on", body: "If a colleague holds a document – your insurer, your health and safety lead – you can send it to them to complete without giving them access to the rest of your portal." },
  { key: 'account',     route: '/portal/compliance', target: '#t-settings',    title: 'Your account menu',             body: 'Find your details, help and contact, change your password, or replay this tour any time from here.' },
  { key: 'done',        route: '/portal',                                   title: "You're all set",                body: "That's the tour. You can replay it whenever you like from your account menu.", final: true },
];
